// What a promoter shares to get brands booking an event: the booking link,
// ready-made Instagram images (feed post and story) and a QR code, all as
// downloads. Shown in ShareEventModal: right after publishing, and from the
// share button on the My Events cards.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { PulseLoader } from 'react-spinners';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline';

import { useToast } from 'context/toastContext';
import { applyLinkFor, canShareApplyLink } from 'utils/applyLink';
import { trackAction } from 'utils/analytics';
import getEventStatus, { isEventCancelled } from 'utils/eventStatus';
import { canvasToFile, loadShareAssets, renderShareImage } from 'utils/shareImage';

import { PortugalFlag, UkFlag } from './flags';

// Big enough to print on an A4 poster without looking soft.
const QR_OPTIONS = { errorCorrectionLevel: 'M', margin: 2, width: 1024 };

const fileBase = (event) =>
  `there2b-${String(event?.slug || event?.id || 'evento').slice(0, 60)}`;

const download = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  download(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Brands can only book before the event starts, and never a cancelled one.
// Day granularity is enough for a heads-up; the API has the final word.
const takesBookings = (event) =>
  !isEventCancelled(event) &&
  String(event?.status || '').toLowerCase() !== 'completed' &&
  getEventStatus(event?.date) === 'upcoming';

// A row on phones (thumbnail · name · button), a column from sm up.
function DownloadCard({ title, hint, preview, previewAlt, children }) {
  return (
    <div className="min-w-0 flex sm:flex-col items-center gap-3 sm:gap-2 rounded-xl ring-1 ring-gray-200 bg-white p-2.5 sm:p-3">
      <div className="w-16 h-16 sm:w-full sm:h-44 shrink-0 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
        {preview ? (
          <img
            src={preview}
            alt={previewAlt}
            className="max-w-full max-h-full object-contain rounded shadow-sm"
          />
        ) : (
          <PulseLoader color="#FFBD59" size={6} />
        )}
      </div>
      <div className="flex-1 sm:flex-none min-w-0 sm:w-full sm:text-center">
        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
        <p className="text-[11px] text-gray-500 truncate">{hint}</p>
      </div>
      <div className="shrink-0 sm:w-full flex flex-col items-center gap-1">{children}</div>
    </div>
  );
}

const LANGS = [
  { code: 'pt', Flag: PortugalFlag },
  { code: 'en', Flag: UkFlag },
];

// Which language the link, its preview and the images are in.
function LanguageSwitch({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold text-gray-700">{t('share_kit.language')}</span>
      <div
        role="radiogroup"
        aria-label={t('share_kit.language')}
        className="inline-flex rounded-lg bg-gray-100 p-1 gap-1"
      >
        {LANGS.map(({ code, Flag }) => {
          const active = value === code;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={t(`share_kit.lang_${code}`)}
              onClick={() => onChange(code)}
              className={[
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition',
                active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800',
              ].join(' ')}
            >
              <Flag className="w-5 h-auto rounded-[2px] ring-1 ring-black/10" />
              {code.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DownloadButton = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="sm:w-full px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 bg-gray-900 text-white hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
  >
    <ArrowDownTrayIcon className="w-4 h-4 shrink-0" />
    <span className="truncate">{children}</span>
  </button>
);

export default function ShareKit({ event }) {
  const { t, i18n } = useTranslation();
  const addToast = useToast();
  // Starts in the promoter's own language; they can switch to share in the
  // other one.
  const [lang, setLang] = useState(() =>
    i18n.language?.startsWith('en') ? 'en' : 'pt'
  );

  const shareable = canShareApplyLink(event);
  const open = shareable && takesBookings(event);
  const link = useMemo(
    () => applyLinkFor(event, { source: 'link', lang }),
    [event, lang]
  );
  // The QR code carries its own source so it can be told apart from the
  // copied link later on.
  const qrLink = useMemo(
    () => applyLinkFor(event, { source: 'qr', lang }),
    [event, lang]
  );
  const eventName = event?.name || '';

  const [copied, setCopied] = useState(false);
  const [qrPng, setQrPng] = useState(null);
  const [images, setImages] = useState({}); // format → { file, url }
  const [imagesFailed, setImagesFailed] = useState(false);
  // Wallpaper, logo and fonts for this event: fetched once, reused when the
  // language changes.
  const assetsRef = useRef({ key: null, promise: null });

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  useEffect(() => {
    if (!shareable || !qrLink) return undefined;
    let cancelled = false;
    QRCode.toDataURL(qrLink, QR_OPTIONS)
      .then((url) => !cancelled && setQrPng(url))
      .catch((err) => console.error('QR generation failed:', err));
    return () => {
      cancelled = true;
    };
  }, [shareable, qrLink]);

  // Re-render the images when anything drawn on them changes.
  const renderKey = [
    event?.id,
    event?.name,
    event?.city,
    event?.location,
    event?.avatar,
    event?.landscapeImage,
    JSON.stringify(event?.date || null),
    lang,
  ].join('|');

  useEffect(() => {
    if (!shareable || !event?.id) return undefined;
    let cancelled = false;
    const urls = [];
    setImages({});
    setImagesFailed(false);

    (async () => {
      try {
        const assetsKey = [event.id, event.avatar, event.landscapeImage].join('|');
        if (assetsRef.current.key !== assetsKey) {
          assetsRef.current = { key: assetsKey, promise: loadShareAssets(event) };
        }
        const assets = await assetsRef.current.promise;
        const next = {};
        for (const format of ['post', 'story']) {
          const canvas = renderShareImage({ format, event, assets, lang });
          const file = await canvasToFile(
            canvas,
            `${fileBase(event)}-${format}-${lang}.jpg`
          );
          const url = URL.createObjectURL(file);
          urls.push(url);
          next[format] = { file, url };
        }
        if (!cancelled) setImages(next);
      } catch (err) {
        console.error('Share images failed:', err);
        if (!cancelled) setImagesFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
    // renderKey covers the event fields the images use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderKey, shareable]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      trackAction('share_link_copy', event?.id);
    } catch (err) {
      // Clipboard blocked (http, old browser): the field is selectable.
      addToast(t('share_kit.copy_failed'), 'error');
    }
  };

  const trackDownload = () => trackAction('share_download', event?.id);

  const downloadSvg = async () => {
    trackDownload();
    try {
      const svg = await QRCode.toString(qrLink, { ...QR_OPTIONS, type: 'svg' });
      downloadBlob(
        new Blob([svg], { type: 'image/svg+xml' }),
        `${fileBase(event)}-qr-${lang}.svg`
      );
    } catch (err) {
      console.error('QR SVG failed:', err);
      addToast(t('share_kit.qr_failed'), 'error');
    }
  };

  if (!shareable) {
    return (
      <p className="text-sm rounded-xl bg-amber-50 text-amber-800 px-4 py-3">
        {t('share_kit.draft')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <LanguageSwitch value={lang} onChange={setLang} />

      {!open && (
        <p className="text-sm rounded-xl bg-amber-50 text-amber-800 px-4 py-3">
          {t('share_kit.closed')}
        </p>
      )}

      {/* 1. The booking link */}
      <div>
        <label
          htmlFor={`share-link-${event?.id}`}
          className="block text-xs font-semibold text-gray-700 mb-1.5"
        >
          {t('share_kit.link_label')}
        </label>
        <div className="flex gap-2">
          <input
            id={`share-link-${event?.id}`}
            type="text"
            readOnly
            value={link || ''}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 py-2.5 rounded-lg ring-1 ring-gray-200 bg-gray-50 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            type="button"
            onClick={copyLink}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 bg-gray-900 text-white hover:bg-black shrink-0"
          >
            {copied ? (
              <CheckIcon className="w-4 h-4" />
            ) : (
              <Square2StackIcon className="w-4 h-4" />
            )}
            {copied ? t('share_kit.copied') : t('share_kit.copy')}
          </button>
        </div>
      </div>

      {/* 2–4. Downloads */}
      {imagesFailed && (
        <p className="text-sm rounded-xl bg-rose-50 text-rose-700 px-4 py-3">
          {t('share_kit.images_error')}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        <DownloadCard
          title={t('share_kit.post')}
          hint={t('share_kit.post_hint')}
          preview={images.post?.url}
          previewAlt={t('share_kit.post_alt', { event: eventName })}
        >
          <DownloadButton
            disabled={!images.post}
            onClick={() => {
              trackDownload();
              downloadBlob(images.post.file, images.post.file.name);
            }}
          >
            {t('share_kit.download')}
          </DownloadButton>
        </DownloadCard>

        <DownloadCard
          title={t('share_kit.story')}
          hint={t('share_kit.story_hint')}
          preview={images.story?.url}
          previewAlt={t('share_kit.story_alt', { event: eventName })}
        >
          <DownloadButton
            disabled={!images.story}
            onClick={() => {
              trackDownload();
              downloadBlob(images.story.file, images.story.file.name);
            }}
          >
            {t('share_kit.download')}
          </DownloadButton>
        </DownloadCard>

        <DownloadCard
          title={t('share_kit.qr')}
          hint={t('share_kit.qr_hint')}
          preview={qrPng}
          previewAlt={t('share_kit.qr_alt', { event: eventName })}
        >
          <DownloadButton
            disabled={!qrPng}
            onClick={() => {
              trackDownload();
              download(qrPng, `${fileBase(event)}-qr-${lang}.png`);
            }}
          >
            {t('share_kit.download')}
          </DownloadButton>
          <button
            type="button"
            onClick={downloadSvg}
            className="text-[11px] font-semibold text-gray-500 hover:text-gray-900 underline underline-offset-2 whitespace-nowrap"
          >
            {t('share_kit.svg')}
          </button>
        </DownloadCard>
      </div>
    </div>
  );
}
